import { Handle, Position } from "@xyflow/react";
import React from "react";
import ChecklistIcon from '@mui/icons-material/Checklist';
import { Box, Typography, useTheme } from "@mui/material";
import ReusableNodes, { NodeContent, NodeError, NodeHandles, NodeHeader, NodeWarning } from "../../../../../components/ReusableNodes";
import { CustomTooltip } from "../../../../../components/TooltipCustomStyle";
import TooltipStatusTitle from "../../../../../components/TooltipStatusTitle";
import ErrorIcon from '@mui/icons-material/Error';
import isSelectedAndHighlight from "../../../../../utils/isSelectedAndHighlight";
// import DrgIcon from "../../drgicons/DrgIcon";

export default function CustomSelectionNode({ data, selected, id, onNodeClick }) {

    const theme = useTheme();

    const label = data.general_properties.label;
    const description = data.general_properties.description;

    const handleNodeClick = (event) => {
        event.stopPropagation(); // ป้องกัน event bubbling
        if (onNodeClick) {
            onNodeClick(event, id);
        }
    };

    const nodeContent = () => {
        const selectedItems = data?.general_properties?.resource_schema || [];
        const MAX_VISIBLE_SELECTED_ITEMS = 4;
        const SELECTION_ITEM_HEIGHT_PX = 24;
        const SELECTION_HEADER_HEIGHT_PX = 40;
        const checkedItems = selectedItems.filter((item) => item?.checked === true);
        const hasSelectedItems = checkedItems.length > 0;
        const shouldEnableScroll = checkedItems.length > MAX_VISIBLE_SELECTED_ITEMS;
        const selectionContainerMaxHeight = (MAX_VISIBLE_SELECTED_ITEMS * SELECTION_ITEM_HEIGHT_PX) + SELECTION_HEADER_HEIGHT_PX;
        if (!hasSelectedItems) {
            return null;
        }

        const handleSelectionWheelCapture = (event) => {
            // Prevent React Flow canvas zoom when wheel is used over the selection list.
            event.stopPropagation();
            if (event.nativeEvent?.stopImmediatePropagation) {
                event.nativeEvent.stopImmediatePropagation();
            }
        };

        return (
            <div className="border border-gray-300 rounded-md overflow-hidden">
                <div
                    className={`nowheel ${shouldEnableScroll ? 'overflow-y-auto' : ''}`}
                    style={shouldEnableScroll ? { maxHeight: `${selectionContainerMaxHeight}px` } : undefined}
                    onWheelCapture={handleSelectionWheelCapture}
                >
                    <Typography
                        variant="body2"
                        className={`px-2 py-2 ${shouldEnableScroll ? 'sticky top-0 z-10 bg-white border-b border-gray-200 rounded-t-md' : 'pb-1'}`}
                        sx={{ fontWeight: 600 }}
                    >
                        Selected
                    </Typography>
                    <div className="px-2 pb-2">
                        {checkedItems.map((item, index) => {
                            return (
                                <Typography key={index} variant="body2" className="font-normal leading-6">
                                    • {item.name}
                                </Typography>
                            );
                        })}
                    </div>
                </div>
            </div>
        )
    };

    const nodeHandles = ({ headerAnchorY } = {}) => {
        const handleClassName = `drg-handle ${selected ? 'drg-selected-handle' : ''}`;

        const sideHandleStyle = typeof headerAnchorY === 'number'
            ? { top: `${headerAnchorY}px` }
            : undefined;

        return (
            <div>
                <Handle
                    id="top-target"
                    type="target"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="top-source"
                    type="source"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-target"
                    type="target"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-source"
                    type="source"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="left-target"
                    type="target"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="left-source"
                    type="source"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-target"
                    type="target"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-source"
                    type="source"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
            </div>
        )
    };

    return (
        <ReusableNodes
            selected={selected}
            highlighted={data?.highlighted}
            focusMode={data?.focusMode}
            backgroundColor="#ffffff"
            onClick={handleNodeClick}
        >
            {data?.warning === true && (
                <NodeWarning>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Warning" listDetails={data?.warning_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.warning.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.warning.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeWarning>
            )}
            {(data?.error === true || data?.error_text.length > 0) && (
                <NodeError>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Error" listDetails={data?.error_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.error.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.error.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeError>
            )}
            <NodeHeader>
                <div className="drg-icon bg-gray-100">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="block h-6 w-6"
                    >
                        <path stroke="none" d="M21 11.999928C21 12.216864 20.921016 12.425016 20.780328 12.578448C20.639712 12.731808 20.448912 12.818064 20.250048 12.818064H12.000216C11.80128 12.818064 11.610552 12.731808 11.469864 12.578448C11.329248 12.425016 11.250192 12.216864 11.250192 11.999928C11.250192 11.78292 11.329248 11.57484 11.469864 11.421408C11.610552 11.267976 11.80128 11.181792 12.000216 11.181792H20.250048C20.448912 11.181792 20.639712 11.267976 20.780328 11.421408C20.921016 11.57484 21 11.78292 21 11.999928ZM12.000216 6.272983H20.250048C20.448912 6.272983 20.639712 6.186785 20.780328 6.03336C20.921016 5.879928 21 5.671834 21 5.454847C21 5.237868 20.921016 5.029774 20.780328 4.876342C20.639712 4.722917 20.448912 4.636718 20.250048 4.636718H12.000216C11.80128 4.636718 11.610552 4.722917 11.469864 4.876342C11.329248 5.029774 11.250192 5.237868 11.250192 5.454847C11.250192 5.671834 11.329248 5.879928 11.469864 6.03336C11.610552 6.186785 11.80128 6.272983 12.000216 6.272983ZM20.250048 17.726808H12.000216C11.80128 17.726808 11.610552 17.812992 11.469864 17.966424C11.329248 18.119856 11.250192 18.328008 11.250192 18.544944C11.250192 18.761952 11.329248 18.970032 11.469864 19.123464C11.610552 19.276896 11.80128 19.36308 12.000216 19.36308H20.250048C20.448912 19.36308 20.639712 19.276896 20.780328 19.123464C20.921016 18.970032 21 18.761952 21 18.544944C21 18.328008 20.921016 18.119856 20.780328 17.966424C20.639712 17.812992 20.448912 17.726808 20.250048 17.726808ZM7.719679 3.239759L6.220027 4.876306C5.69879 5.445127 4.802088 5.44499 4.28101 4.876025C4.140286 4.722506 3.949421 4.636265 3.750398 4.636265C3.551381 4.636265 3.360514 4.722506 3.219787 4.876025C3.07906 5.029536 3 5.237746 3 5.454847C3 5.671956 3.07906 5.880166 3.219787 6.033677L4.719749 7.669942C4.789402 7.74601 4.872122 7.806353 4.963166 7.84753C5.05421 7.888699 5.151806 7.909889 5.250367 7.909889C5.348921 7.909889 5.446517 7.888699 5.537561 7.84753C5.628612 7.806353 5.711326 7.74601 5.780978 7.669942L8.780909 4.397412C8.921633 4.243901 9.000696 4.035691 9.000696 3.81859C9.000696 3.601484 8.921633 3.393273 8.780909 3.239759C8.640178 3.086244 8.449313 3 8.250298 3C8.051275 3 7.86041 3.086244 7.719679 3.239759ZM7.719679 9.784812L6.220027 11.421336C5.69879 11.990208 4.802088 11.990064 4.28101 11.421048C4.140286 11.267544 3.949421 11.181288 3.750398 11.181288C3.551381 11.181288 3.360514 11.267544 3.219787 11.421048C3.07906 11.574624 3 11.782776 3 11.999928C3 12.107424 3.01941 12.21384 3.057121 12.3132C3.094832 12.412488 3.150106 12.502704 3.219787 12.578736L4.719749 14.215008C4.789402 14.29104 4.872122 14.351376 4.963166 14.39256C5.05421 14.433744 5.151806 14.454912 5.250367 14.454912C5.348921 14.454912 5.446517 14.433744 5.537561 14.39256C5.628612 14.351376 5.711326 14.29104 5.780978 14.215008L8.780909 10.942464C8.921633 10.78896 9.000696 10.580736 9.000696 10.363656C9.000696 10.14654 8.921633 9.93833 8.780909 9.784812C8.640178 9.631301 8.449313 9.545052 8.250298 9.545052C8.051275 9.545052 7.86041 9.631301 7.719679 9.784812ZM7.719679 16.329864L6.220027 17.966424C5.69879 18.535224 4.802088 18.53508 4.28101 17.966136C4.211328 17.890104 4.128607 17.82984 4.037563 17.788656C3.946519 17.747544 3.848945 17.726376 3.750398 17.726376C3.651856 17.726376 3.554277 17.747544 3.463234 17.788656C3.372191 17.82984 3.289468 17.890104 3.219787 17.966136C3.150106 18.042168 3.094832 18.132384 3.057121 18.231672C3.01941 18.331032 3 18.437448 3 18.544944C3 18.65244 3.01941 18.758928 3.057121 18.858216C3.094832 18.957504 3.150106 19.047792 3.219787 19.123824L4.719749 20.760024C4.789402 20.836128 4.872122 20.896464 4.963166 20.937648C5.05421 20.978832 5.151806 21 5.250367 21C5.348921 21 5.446517 20.978832 5.537561 20.937648C5.628612 20.896464 5.711326 20.836128 5.780978 20.760024L8.780909 17.487552C8.921633 17.333976 9.000696 17.125824 9.000696 16.908672C9.000696 16.691592 8.921633 16.483368 8.780909 16.329864C8.640178 16.17636 8.449313 16.090104 8.250298 16.090104C8.051275 16.090104 7.86041 16.17636 7.719679 16.329864Z" fill="#3B67E2" />
                    </svg>
                </div>
                <div className="drg-label">
                    <Typography variant="body2" className="text-gray-900" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: 14 }}>
                        {description}
                    </Typography>
                </div>
            </NodeHeader>
            <NodeContent>
                {nodeContent()}
            </NodeContent>
            <NodeHandles>
                {nodeHandles}
            </NodeHandles>
        </ReusableNodes>
    );
}